// The army builder: three picks (an anchor, a block of three identical core units, a striker), each
// from 2 or 3 options. The options come from the built-in teams' slots (slot 1 anchors, 2–4 core,
// 5 strikers) and are drawn from the league seed, the player and the pick, so they are the same on
// every device and can't be rerolled by reloading.
import { TEAMS } from '../games/capture-the-flag/units';
import { encodeTeam } from '../games/capture-the-flag/teamcode';
import type { UnitType } from '../games/capture-the-flag/types';
import type { Army } from './api';

export type Role = 'Anchor' | 'Core' | 'Striker';
export const PICKS: Role[] = ['Anchor', 'Core', 'Striker'];
export const SLOT_ROLES: Role[] = ['Anchor', 'Core', 'Core', 'Core', 'Striker'];

function pool(slots: number[]): UnitType[] {
  const seen = new Map<string, UnitType>();
  for (const t of Object.values(TEAMS)) for (const i of slots) {
    const u = t.squad[i];
    if (u && !seen.has(u.name)) seen.set(u.name, u);
  }
  return Array.from(seen.values());
}
export const POOLS: Record<Role, UnitType[]> = { Anchor: pool([0]), Core: pool([1, 2, 3]), Striker: pool([4]) };

function hash(...parts: (string | number)[]): number {
  let h = 2166136261;
  const s = parts.join('|');
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The options for one pick.
export function offers(seed: number, playerId: string, pick: number, choices: number): UnitType[] {
  const list = POOLS[PICKS[pick]].slice();
  const r = rng(hash(seed, playerId, pick));
  for (let i = list.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [list[i], list[j]] = [list[j], list[i]]; }
  return list.slice(0, Math.min(choices, list.length));
}

const byName = (name: string) => [...POOLS.Anchor, ...POOLS.Core, ...POOLS.Striker].find(u => u.name === name);

// The army from three picks (unit type names) and optional nicknames for the five units.
export function buildArmy(name: string, picks: string[], nicks: string[] = []): Army {
  const types = [picks[0], picks[1], picks[1], picks[1], picks[2]].map(n => byName(n));
  if (types.some(t => !t)) throw new Error('Pick all three first.');
  const squad: UnitType[] = types.map((t, i) => ({ ...(t as UnitType), name: (nicks[i] ?? '').trim() || (t as UnitType).name, zone: (t as UnitType).zone.map(z => [z[0], z[1]] as [number, number]), traits: [...(t as UnitType).traits] }));
  // Two different kinds of unit with the same letter (Mage and Militia) get two letters, so the board
  // can tell them apart.
  squad.forEach((u, i) => {
    const clash = squad.some((v, j) => j !== i && v.letter === u.letter && types[j]!.name !== types[i]!.name);
    if (clash && i !== 1 && i !== 2 && i !== 3) u.letter = (types[i] as UnitType).name.slice(0, 2);
  });
  return {
    name,
    code: encodeTeam({ name, squad }),
    units: types.map((t, i) => ({ role: SLOT_ROLES[i], type: (t as UnitType).name, nick: (nicks[i] ?? '').trim() })),
  };
}

// A bot's army: the first option of each pick.
export function botArmy(seed: number, playerId: string, choices: number, botName: string): Army {
  const picks = [0, 1, 2].map(i => offers(seed, playerId, i, choices)[0].name);
  return buildArmy(`${botName}'s army`, picks);
}

// Built-in teams as league armies ("same team for everyone", or "each picks a team"). Their five units
// follow the builder's slots: an anchor, three core units, a striker.
export const TEAM_KEYS = Object.keys(TEAMS);
export const teamName = (key: string) => TEAMS[key]?.name ?? TEAMS[TEAM_KEYS[0]].name;
// The builder's three picks that make a built-in team (its anchor, core and striker).
export const teamPicks = (key: string) => { const q = (TEAMS[key] ?? TEAMS[TEAM_KEYS[0]]).squad; return [q[0].name, q[1].name, q[4].name]; };
export function teamArmy(key: string, name?: string, nicks: string[] = []): Army {
  return buildArmy(name?.trim() || teamName(key), teamPicks(key), nicks);
}

// A player's army made for them, by the league's army setting: a bot's (the builder's first options,
// or a built-in team drawn from the seed), or everyone's when they all get the same team.
export function serverArmy(settings: { armies?: string; team?: string; choices: number }, seed: number, playerId: string, name: string): Army {
  if (settings.armies === 'fixed') return teamArmy(settings.team ?? TEAM_KEYS[0]);
  if (settings.armies === 'pick') {
    const key = TEAM_KEYS[hash(seed, playerId, 'team') % TEAM_KEYS.length];
    return teamArmy(key);
  }
  return botArmy(seed, playerId, settings.choices, name);
}
